import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useModalNavigation } from "../../context/ModalControllerContext";
import { useEditableMeal } from "../../context/ModalEntryContext";
import { calculateNutrientsByAmount } from "../../helpers";
import useSmartDropdown from "../../hooks/ui/useSmartDropdown";
import useHandleClickOutside from "../../hooks/utils/useHandleClickOutside";
import ModalHeader from "../components/ModalHeader";

const MIN_SERVINGS = 0.1;
const PRESET_SERVINGS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

// HELPERS

const round = (value, precision = 2) => {
	const multiplier = 10 ** precision;
	return Math.round(value * multiplier) / multiplier;
};

const ModalRecipeServings = () => {
	const { editableMeal, setEditableMeal, templateSnapshot, setTemplateSnapshot } = useEditableMeal();
	const { replaceView, goBack, close } = useModalNavigation();

	// STATE

	const [rawServings, setRawServings] = useState(String(editableMeal.servings ?? 1));
	const [isCustom, setIsCustom] = useState(!PRESET_SERVINGS.includes(editableMeal.servings ?? 1));

	// REFS
	const menuRef = useRef(null);

	// DROPDOWN
	const { triggerRef, dropdownRef, menuStyle, hasPosition, open, setOpen } = useSmartDropdown({
		scrollContainerSelector: ".recipe-servings",
	});

	useHandleClickOutside([dropdownRef, triggerRef, menuRef], open, setOpen);

	// DERIVED

	const parsedServings = parseFloat(rawServings.replace(",", "."));
	const isInvalidServings = Number.isNaN(parsedServings) || parsedServings < MIN_SERVINGS;
	const normalizedServings = isInvalidServings ? null : round(parsedServings, 4);
	const recipeImage = templateSnapshot?.image || "/assets/images/static/recipes/default-image-recipe.jpg";

	// Calculate total kcal from all items and scale by servings
	const totalRecipeKcal = templateSnapshot?.items?.reduce((sum, item) => sum + (item.kcal || 0), 0) || 0;
	const scaledKcal =
		normalizedServings && totalRecipeKcal
			? round(totalRecipeKcal * normalizedServings, 0)
			: round(totalRecipeKcal, 0);

	// Display value for dropdown
	const displayValue = isCustom ? "Egen portionsstorlek" : rawServings;

	// HANDLERS

	const handleSelectPreset = (amount) => {
		setRawServings(String(amount));
		setIsCustom(false);
		setOpen(false);
	};

	const handleSelectCustom = () => {
		setIsCustom(true);
		setOpen(false);
	};

	const handleCustomInput = (e) => {
		setRawServings(e.target.value);
	};

	const handleConfirm = () => {
		if (normalizedServings === null) return;
		if (!templateSnapshot?.items) return;

		const scaledItems = templateSnapshot.items.map((item) => {
			const scaledAmount = round(item.amount * normalizedServings, 2);
			const recalculated = calculateNutrientsByAmount(item.base, scaledAmount, item.unit);

			const { _recipeOrigin, ...rest } = item;

			return {
				...rest,
				amount: scaledAmount,
				...recalculated,
			};
		});

		setTemplateSnapshot({
			type: "recipe",
			items: scaledItems,
			name: templateSnapshot.name,
		});

		setEditableMeal((prev) => {
			return {
				...prev,
				items: [...prev.items.filter((item) => !item._recipeOrigin), ...scaledItems],
				servings: normalizedServings,
			};
		});

		replaceView("editMeal");
	};

	// SYNC WHEN MEAL CHANGES

	useEffect(() => {
		setRawServings(String(editableMeal.servings ?? 1));
	}, [editableMeal.servings]);

	return (
		<>
			<ModalHeader title="Välj portioner" onGoBack={goBack} onClose={close} />

			<div className="modal-body recipe-servings">
				<div className="left">
					<div className="recipe-image">
						<img src={recipeImage} alt="Receptbild" />
					</div>
				</div>

				<div className="right">
					<div className="recipe-servings-title">
						<span className="bold-title">Recept:</span>
						<span>
							{templateSnapshot?.name ?? ""} ({scaledKcal} kcal)
						</span>
					</div>

					<div className="input-fields">
						<div className="servings-dropdown-wrapper">
							<span className="dropdown-label">
								<strong>Portioner</strong> (antal):
							</span>

							<div className="dropdown" ref={dropdownRef}>
								<div
									className="dropdown-selected"
									ref={triggerRef}
									onClick={() => setOpen((p) => !p)}
								>
									<span className="servings-value">{displayValue}</span>
									<span className="dropdown-icon">
										<img
											src="/assets/images/diary-icons/chevron-down.svg"
											alt="Välj portioner"
										/>
									</span>
								</div>

								{open &&
									hasPosition &&
									createPortal(
										<ul
											ref={menuRef}
											className="dropdown-servings"
											style={menuStyle}
											onClick={(e) => e.stopPropagation()}
										>
											{PRESET_SERVINGS.map((amount) => (
												<li
													key={amount}
													onClick={() => handleSelectPreset(amount)}
													className={
														displayValue === String(amount) ? "active" : ""
													}
												>
													{amount}
												</li>
											))}
											<li
												onClick={handleSelectCustom}
												className={isCustom ? "active" : ""}
											>
												Egen portionsstorlek
											</li>
										</ul>,
										document.body,
									)}
							</div>
						</div>

						<input
							className={`modal-input custom-servings-input ${isCustom ? "visible" : ""}`}
							type="number"
							inputMode="decimal"
							value={rawServings}
							min={MIN_SERVINGS}
							onChange={handleCustomInput}
							placeholder="Ange portioner..."
						/>
					</div>
				</div>
			</div>

			<div className="modal-footer">
				<button className="btn-green" onClick={handleConfirm} disabled={normalizedServings === null}>
					Fortsätt
				</button>
			</div>
		</>
	);
};

export default ModalRecipeServings;
